import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../lib/firebase';
import { collection, getDocs } from 'firebase/firestore';

interface PlayerRecord {
    id: string;
    gamerTag: string;
    game: string;
    main?: string;
    alts?: string;
    bestResult?: string;
    season: string;
}

interface EventResult {
    id: string;
    eventName: string;
    eventDate: string;
    location: string;
    game: string;
    placement: string;
    players?: string;
}

export default function RecordsPage() {
    const [activeTab, setActiveTab] = useState<'players' | 'events'>('players');
    const [players, setPlayers] = useState<PlayerRecord[]>([]);
    const [events, setEvents] = useState<EventResult[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        async function loadData() {
            try {
                const [playersSnap, eventsSnap] = await Promise.all([
                    getDocs(collection(db, 'playerRecords')),
                    getDocs(collection(db, 'eventResults'))
                ]);
                setPlayers(playersSnap.docs.map(d => ({ id: d.id, ...d.data() } as PlayerRecord)));
                setEvents(eventsSnap.docs.map(d => ({ id: d.id, ...d.data() } as EventResult)));
            } catch (err) {
                console.error('Error loading records:', err);
            } finally {
                setLoading(false);
            }
        }
        loadData();
    }, []);

    if (loading) {
        return (
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 flex justify-center">
                <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            {/* Header */}
            <div className="text-center mb-12 animate-fade-in">
                <h1 className="text-4xl md:text-5xl font-display font-bold text-white mb-4">
                    📊 <span className="text-primary-500">Records & Results</span>
                </h1>
                <p className="text-secondary-400 text-lg">Peck Pirates Competitive History</p>
            </div>

            {/* Tabs */}
            <div className="flex justify-center gap-2 mb-8">
                <button
                    onClick={() => setActiveTab('players')}
                    className={`px-6 py-2 rounded-lg font-medium transition-colors ${activeTab === 'players'
                            ? 'bg-primary-500 text-white'
                            : 'bg-secondary-800 text-secondary-300 hover:bg-secondary-700'
                        }`}
                >
                    🎮 Player Records
                </button>
                <button
                    onClick={() => setActiveTab('events')}
                    className={`px-6 py-2 rounded-lg font-medium transition-colors ${activeTab === 'events'
                            ? 'bg-primary-500 text-white'
                            : 'bg-secondary-800 text-secondary-300 hover:bg-secondary-700'
                        }`}
                >
                    🏆 Event Results
                </button>
            </div>

            {/* Player Records */}
            {activeTab === 'players' && (
                <section className="mb-12">
                    {players.length === 0 ? (
                        <div className="text-center py-12 text-secondary-400">
                            <p className="text-lg">No player records yet.</p>
                            <p className="text-sm mt-2">Check back soon or contact the coach for info!</p>
                        </div>
                    ) : (
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {players.map((player, idx) => (
                                <div
                                    key={player.id}
                                    className="card animate-slide-up"
                                    style={{ animationDelay: `${idx * 0.05}s` }}
                                >
                                    <div className="flex items-center justify-between mb-3">
                                        <h3 className="text-lg font-bold text-white">{player.gamerTag}</h3>
                                        <span className="text-xs bg-secondary-700 px-2 py-0.5 rounded text-secondary-300">
                                            {player.season}
                                        </span>
                                    </div>

                                    <div className="text-sm text-secondary-400 mb-3">{player.game}</div>

                                    {player.main && (
                                        <div className="mb-2 flex flex-wrap gap-1">
                                            <span className="badge-primary">{player.main}</span>
                                            {player.alts && player.alts.split(',').map((alt, i) => (
                                                <span key={i} className="badge bg-secondary-700 text-secondary-300">
                                                    {alt.trim()}
                                                </span>
                                            ))}
                                        </div>
                                    )}

                                    {player.bestResult && (
                                        <div className="text-sm text-primary-400 mt-3">
                                            ⭐ {player.bestResult}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </section>
            )}

            {/* Event Results */}
            {activeTab === 'events' && (
                <section className="mb-12">
                    {events.length === 0 ? (
                        <div className="text-center py-12 text-secondary-400">
                            <p className="text-lg">No event results yet.</p>
                            <p className="text-sm mt-2">Results will appear here after competitions!</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {events.map((event, idx) => (
                                <div
                                    key={event.id}
                                    className="card animate-slide-up"
                                    style={{ animationDelay: `${idx * 0.05}s` }}
                                >
                                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                                        <div>
                                            <h3 className="text-lg font-bold text-white">{event.eventName}</h3>
                                            <div className="text-sm text-secondary-400 mt-1">
                                                {event.eventDate && <span>{event.eventDate}</span>}
                                                {event.location && <span> • {event.location}</span>}
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-primary-400 font-bold text-lg">{event.placement}</div>
                                            <div className="text-sm text-secondary-400">{event.game}</div>
                                        </div>
                                    </div>
                                    {event.players && (
                                        <div className="mt-3 pt-3 border-t border-secondary-700">
                                            <span className="text-xs text-secondary-500">Players: </span>
                                            <span className="text-sm text-secondary-300">{event.players}</span>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </section>
            )}

            {/* Contact */}
            <div className="mt-12 text-center">
                <p className="text-secondary-500">
                    Questions? Contact Coach Brian Anklam<br />
                    <a href="mailto:anklamb@peckschools.net" className="text-primary-400 hover:underline">anklamb@peckschools.net</a>
                    {' • '}
                    <a href="tel:8103780645" className="text-primary-400 hover:underline">(810) 378-0645</a>
                </p>
            </div>

            {/* Nav */}
            <div className="flex justify-center gap-4 mt-8">
                <Link to="/championships" className="btn-secondary">← Championships</Link>
                <Link to="/games" className="btn-primary">Our Games →</Link>
            </div>
        </div>
    );
}
